import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AgGrid, WppEmptyNothingFound, WppTypography } from 'buildingBlocks';
import { User, EnhancedStrategy, Strategy, ToastConfig, Flight } from 'utils/types';
import HeliosReportModal from 'components/StrategyActions/HeliosReportModal';
import { strategyEmptyMessageContainer } from 'containers/StrategiesList/style';
import { ControlTestFlight, initialDspModalState } from 'containers/StrategiesList/constants';
import useSideModalHideFreshdesk from 'utils/hooks/useSideModalHideFreshdesk';
import TestStrategyModal from 'components/StrategyActions/TestStrategyModal';
import { updateClientTestForStrategy } from 'containers/StrategyAnalytics/actions';
import { getTestControlObject } from 'containers/StrategiesList/utils';
import { crossPlatformStratId } from 'containers/StrategyWizard/utils';
import CrossPlatformTestStrategyModal from 'components/StrategyActions/CrossPlatformTestStrategyModal';
import DspQuickLinkModal from './DspQuickLinkModal';
import { strategiesTableColumns } from './StrategyTableColumns';

type StrategyTableState = {
  isOpen: boolean
  strategy: EnhancedStrategy
};

type DspModalState = {
  dspId: number | null
  flights: Array<Flight>
};

type StrategyTableProps = {
  strategyCount: number
  filter: {}
  strategies: Array<EnhancedStrategy>
  heliosDownloadReport: (...args: Array<any>) => void
  downloadingReport: boolean
  downloadedReport: number
  toggleDeleteModal: Function
  user: User
  innerRef: any
  showToast: (config: ToastConfig) => void
  loadingStrategy?: boolean
};

const StrategyTable: React.FC<StrategyTableProps> = (props: StrategyTableProps) => {
  const {
    user, filter, toggleDeleteModal, loadingStrategy, heliosDownloadReport,
    downloadedReport, downloadingReport, innerRef, strategies, strategyCount, showToast,
  } = props;
  const dispatch = useDispatch();
  const [state, setState] = useState<StrategyTableState>({ isOpen: false, strategy: null });
  const [openTestModal, setOpenTestModal] = useState<boolean>(false);
  const [selectedTestStrat, setSelectedTestStrat] = useState<EnhancedStrategy>(null);
  const [dspModalState, setDspModalState] = useState<DspModalState>(initialDspModalState);
  const [exportCsvLoading, setExportCsvLoading] = useState<boolean>(false);
  const [isDspQuickLinkModalOpen, setIsDSPQuickLinkModalOpen] = useState<boolean>(false);

  const rowData = _.lt(strategyCount, 1) ? [] : strategies;

  // hide freshdesk button when dsp quick links modal or test strategy modal is open
  useSideModalHideFreshdesk(isDspQuickLinkModalOpen || openTestModal);

  const handleDeleteStrategy = (strategy: Strategy) => {
    toggleDeleteModal(strategy);
  };

  const onCloseModal = () => {
    setState((prevState: any) => ({ ...prevState, isOpen: false, strategy: null }));
  };

  const onCloseDspModal = () => {
    setIsDSPQuickLinkModalOpen(false);
    setDspModalState(initialDspModalState);
  };

  const onDspClick = (dspId: number, flights: Array<Flight>) => {
    setIsDSPQuickLinkModalOpen(true);
    setDspModalState({ dspId, flights });
  };

  const handleDownloadStrategy = (data: EnhancedStrategy) => {
    if (data) {
      setState((prevState: any) => ({ ...prevState, isOpen: true, strategy: { ...data, flights: _.values(data.flights) } }));
    }
  };

  const noRowsMessage = `No strategy found. ${_.isEmpty(_.keys(_.omitBy(filter, _.isEmpty))) ? '' : ' Please redefine your filters'}`;

  const updateStrategyAsTest = (strategy: Strategy | EnhancedStrategy, controlObjects?: Record<string, Array<ControlTestFlight>>, isCrossPlatform?: boolean) => {
    const { id, clientTest } = strategy;
    const flight: Flight = _.first(_.values(_.get(strategy, 'flights')));
    const controlObjectMapping = isCrossPlatform ? _.flatten(_.values(controlObjects)) : getTestControlObject(flight, controlObjects);
    dispatch(updateClientTestForStrategy(id, !clientTest, true, showToast, controlObjectMapping));
    setOpenTestModal(false);
  };

  const renderAgGrid = () => (
    <AgGrid
      rowHeight={60}
      ref={innerRef}
      rowSelection="multiple"
      noRowsMessage={noRowsMessage}
      loading={loadingStrategy || exportCsvLoading}
      className="strategies-list"
      columnDefs={strategiesTableColumns(
        user,
        rowData,
        handleDeleteStrategy,
        handleDownloadStrategy,
        setOpenTestModal,
        setSelectedTestStrat,
        showToast,
        onDspClick,
        setExportCsvLoading,
      )}
      rowData={rowData}
      suppressRowClickSelection
    />
  );

  const renderEmptyMessage = () => (
    <div style={strategyEmptyMessageContainer}>
      <WppEmptyNothingFound width={120} />
      <WppTypography tag="p" type="s-body">{noRowsMessage}</WppTypography>
    </div>
  );

  return (
    <>
      {state.isOpen && (
        <HeliosReportModal
          {...state.strategy}
          isOpen={state.isOpen}
          downloadedReport={downloadedReport}
          downloadingReport={downloadingReport}
          heliosDownloadReport={heliosDownloadReport}
          onCancel={onCloseModal}
        />
      )}
      {isDspQuickLinkModalOpen && (
        <DspQuickLinkModal
          isOpen={isDspQuickLinkModalOpen}
          onClose={onCloseDspModal}
          flightsData={dspModalState.flights}
        />
      )}

      {(_.isEmpty(strategies) && !loadingStrategy) ? renderEmptyMessage() : renderAgGrid()}
      {!_.isEqual(_.get(selectedTestStrat, 'strategyType.id'), crossPlatformStratId) ? (
        <TestStrategyModal
          openModal={openTestModal}
          setOpenModal={setOpenTestModal}
          updateStrategyAsTest={updateStrategyAsTest}
          strategy={selectedTestStrat}
        />
      ) : (
        <CrossPlatformTestStrategyModal
          openModal={openTestModal}
          setOpenModal={setOpenTestModal}
          updateStrategyAsTest={updateStrategyAsTest}
          strategy={selectedTestStrat}
          setSelectedTestStrat={setSelectedTestStrat}
        />
      )}
    </>
  );
};

export default StrategyTable;
